import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Short alerts at the bottom of the screen ("Chat closed: find it in Chats → Closed"), each with its own close
// button; they also go away after a while. Anything can raise one with toast().

type Toast = { id: number; text: string; link?: { to: string; label: string }; action?: { label: string; run: () => Promise<unknown> | void }; error?: boolean };
export const toast = (text: string, link?: Toast['link'], action?: Toast['action'], error?: boolean) => window.dispatchEvent(new CustomEvent('olv-toast', { detail: { text, link, action, error } }));

export function Toasts() {
  const [list, setList] = useState<Toast[]>([]);
  useEffect(() => {
    const f = (e: Event) => {
      const t = { id: Date.now() + Math.random(), ...(e as CustomEvent<Omit<Toast, 'id'>>).detail };
      setList((l) => [...l.slice(-2), t]);
      setTimeout(() => setList((l) => l.filter((x) => x.id !== t.id)), 10_000);
    };
    window.addEventListener('olv-toast', f);
    return () => window.removeEventListener('olv-toast', f);
  }, []);
  if (!list.length) return null;
  return (
    <div className="toasts" role="status" aria-live="polite">
      {/* the newest on top, where the eye goes first; older ones below it */}
      {[...list].reverse().map((t) => (
        <div key={t.id} className={`toast${t.error ? ' toast-error' : ''}`}>
          <span>{t.text}</span>
          {t.action ? <button type="button" className="link" onClick={() => { setList((l) => l.filter((x) => x.id !== t.id)); void Promise.resolve(t.action!.run()).catch((e) => toast((e as Error).message, undefined, undefined, true)); }}>{t.action.label}</button> : null}
          {t.link ? <Link className="link" to={t.link.to} onClick={() => setList((l) => l.filter((x) => x.id !== t.id))}>{t.link.label}</Link> : null}
          <button type="button" className="icon-btn" aria-label="Close this alert" onClick={() => setList((l) => l.filter((x) => x.id !== t.id))}>×</button>
        </div>
      ))}
    </div>
  );
}

/**
 * Close (archive) a chat, asked twice: × only raises this alert, and the chat closes when "Close chat" is pressed in
 * it (× on the alert, or letting it fade, keeps the chat). Afterwards: where it went; a failure says so.
 */
export function closeChat(api: (p: string, o: { method: string }) => Promise<unknown>, id: string, title: string, after?: () => unknown) {
  const name = `“${title.length > 48 ? `${title.slice(0, 48)}…` : title}”`;
  toast(`Close ${name}? It will stay in Chats under Closed, and you can reopen it.`, undefined, { label: 'Close chat', run: async () => {
    try { await api(`/portal/threads/${id}/archive`, { method: 'POST' }); } catch (e) { toast(`${name} could not be closed: ${(e as Error).message}`, undefined, undefined, true); return; }
    window.dispatchEvent(new CustomEvent('olv-thread-changed'));
    await after?.();
    toast(`${name} is closed.`, { to: '/chats?tab=closed', label: 'See closed chats' });
  } });
}
